import { apyToAprPercent } from '../../../utils/parsing'
import { YieldFetcher } from '../../types'
import { fetchDefiLlamaApy } from './defillama'

/**
 * Treehouse tAssets — LSTs that run a leveraged staking carry: the
 * deposited LST is posted as collateral on a lending market, the native
 * asset is borrowed against it and converted back into LST. The published
 * rate is therefore `LST staking yield + Market Efficiency Yield`, i.e.
 * the total asset-denominated return, matching how every other LST row
 * here reports (pufETH's DefiLlama pool is the same shape).
 *
 * Do NOT confuse that with the share price: a tAsset is denominated in its
 * UNDERLYING LST (tETH:wstETH, tAVAX:sAVAX, tHYPE:kHYPE), so
 * `convertToAssets` only moves by the MEY leg. The row's underlying is the
 * LST, but its rate is the whole thing.
 */

/** DefiLlama `treehouse-protocol` pools. */
const TETH_POOL_ID = '5762f4a8-bb48-45d6-90ed-2d93d1777169'
const TAVAX_POOL_ID = '40a1e447-058d-4c5b-b567-30543ea185c3'

/** Treehouse's own rate endpoint. Undocumented but stable and public;
 *  the whole body is `{rate, smaApr, total_apr_teth}` where `smaApr` is the
 *  ETH staking leg, `rate` the Market Efficiency Yield, and the total is
 *  their sum — **already a percent APR**, so no APY conversion. tETH only;
 *  there is no tAVAX/tHYPE route and no query parameter that produces one
 *  (`?chain=` is ignored). */
const TREEHOUSE_APY_URL = 'https://api.treehouse.finance/apy'

/**
 * BOTH case variants are emitted deliberately, and this is not cosmetic.
 * The token-list classifier assigns tETH `Treehouse ETH::TETH` on Ethereum
 * (uppercase) but `Treehouse ETH::tETH` on Arbitrum and Base, and the
 * lending join (`intrinsicYields[asset.assetGroup]`) is a case-sensitive
 * object index. Emitting only the lowercase form left **every Ethereum
 * market collateralised with tETH reading 0 % intrinsic yield** — $49.4M on
 * Aave V3, $4.0M on Compound V3 and $1.05M on Euler V2 — which silently
 * under-ranks every tETH loop in the pair book. Same failure as Cap's
 * stcUSD group split; see `cap.ts`.
 */
const TETH_KEYS = ['Treehouse ETH::TETH', 'Treehouse ETH::tETH']

const fetchTethApr = async (): Promise<number> => {
  try {
    const res = await fetch(TREEHOUSE_APY_URL, {
      headers: { accept: 'application/json' },
      signal: AbortSignal.timeout(8000),
    })
    if (!res.ok) throw new Error(`Treehouse HTTP ${res.status}`)
    const json = (await res.json()) as { total_apr_teth?: unknown }
    const apr = json?.total_apr_teth
    if (typeof apr === 'number' && Number.isFinite(apr) && apr > 0) return apr
    throw new Error('Treehouse: no total_apr_teth')
  } catch {
    // DefiLlama reports a compounded APY → APR per pipeline convention.
    return apyToAprPercent(await fetchDefiLlamaApy(TETH_POOL_ID))
  }
}

export const tethFetcher: YieldFetcher = {
  label: 'TETH',
  fetch: async () => {
    const apr = await fetchTethApr()
    return Object.fromEntries(TETH_KEYS.map((k) => [k, apr]))
  },
}

/**
 * Treehouse tAVAX — Avalanche, sAVAX-denominated. DefiLlama only; the
 * protocol's own `/apy` route serves tETH exclusively.
 *
 * tHYPE has no fetcher at all: DefiLlama carries no `treehouse-protocol`
 * pool for it and Treehouse publishes no route, so its row ships with a
 * `supplyRate` of 0 rather than a made-up number. The honest fallback
 * would be an archival `convertToAssets` delta — worth wiring once the
 * vault has more than a few weeks of history (it launched with a share
 * price of 1.0007).
 */
export const tavaxFetcher: YieldFetcher = {
  label: 'TAVAX',
  fetch: async () => ({
    'Treehouse AVAX::tAVAX': apyToAprPercent(
      await fetchDefiLlamaApy(TAVAX_POOL_ID),
    ),
  }),
}
